import { BaseResource } from './base';
import {
  Gym,
  CreateGymDto,
  UpdateGymDto,
  GymStats,
  UpdateGymScheduleDto,
  UpdateGymSocialMediaDto,
  UpdateGymInventorySettingsDto,
  UpdateGymContractSettingsDto,
} from '../models/gyms';
import { RequestOptions } from '../types';

export class GymsResource extends BaseResource {
  private basePath = 'gyms';

  async createGym(data: CreateGymDto, options?: RequestOptions): Promise<Gym> {
    return this.client.post<Gym>(this.basePath, data, options);
  }

  async getOrganizationGyms(options?: RequestOptions): Promise<Gym[]> {
    return this.client.get<Gym[]>(this.basePath, undefined, options);
  }

  async getGym(id: string, options?: RequestOptions): Promise<Gym> {
    return this.client.get<Gym>(`${this.basePath}/${id}`, undefined, options);
  }

  async updateGym(id: string, data: UpdateGymDto, options?: RequestOptions): Promise<Gym> {
    return this.client.put<Gym>(`${this.basePath}/${id}`, data, options);
  }

  async getGymStats(id: string, options?: RequestOptions): Promise<GymStats> {
    return this.client.get<GymStats>(`${this.basePath}/${id}/stats`, undefined, options);
  }

  async toggleGymStatus(id: string, options?: RequestOptions): Promise<Gym> {
    return this.client.put<Gym>(`${this.basePath}/${id}/toggle-status`, undefined, options);
  }

  async updateCurrentGym(
    data: Partial<{
      name?: string;
      address?: string;
      phone?: string;
      email?: string;
      assetId?: string;
    }>,
    options?: RequestOptions,
  ): Promise<Gym> {
    return this.client.put<Gym>(`${this.basePath}/current`, data, options);
  }

  async updateGymSchedule(
    id: string,
    data: UpdateGymScheduleDto,
    options?: RequestOptions,
  ): Promise<Gym> {
    return this.client.put<Gym>(`${this.basePath}/${id}/schedule`, data, options);
  }

  async updateGymSocialMedia(
    id: string,
    data: UpdateGymSocialMediaDto,
    options?: RequestOptions,
  ): Promise<Gym> {
    return this.client.put<Gym>(`${this.basePath}/${id}/social-media`, data, options);
  }

  /**
   * Update current gym inventory settings (stock configuration)
   * Uses gym from context (x-gym-id header)
   * @param data Inventory settings data
   * @param options Request options
   * @returns Updated gym with new settings
   *
   * @example
   * ```typescript
   * const gym = await sdk.gyms.updateInventorySettings({
   *   defaultMinStock: 15,
   *   defaultMaxStock: 1000,
   *   enableLowStockAlerts: true
   * });
   * ```
   */
  async updateInventorySettings(
    data: UpdateGymInventorySettingsDto,
    options?: RequestOptions,
  ): Promise<Gym> {
    return this.client.patch<Gym>(`${this.basePath}/current/inventory-settings`, data, options);
  }

  /**
   * Update current gym contract expiration settings
   * Uses gym from context (x-gym-id header)
   * @param data Contract settings data
   * @param options Request options
   * @returns Updated gym with new settings
   *
   * @example
   * ```typescript
   * const gym = await sdk.gyms.updateContractSettings({
   *   expiringSoonDays: 7,
   *   gracePeriodDays: 0,
   *   enableExpirationNotifications: true
   * });
   * ```
   */
  async updateContractSettings(
    data: UpdateGymContractSettingsDto,
    options?: RequestOptions,
  ): Promise<Gym> {
    return this.client.patch<Gym>(`${this.basePath}/current/contract-settings`, data, options);
  }

  /**
   * Soft delete a gym
   * Cannot delete gym with active contracts or collaborators
   * @param id Gym ID
   * @param options Request options
   * @returns Success response
   *
   * @example
   * ```typescript
   * const result = await sdk.gyms.deleteGym('gym-id');
   * console.log(result.success); // true
   * ```
   */
  async deleteGym(id: string, options?: RequestOptions): Promise<{ success: boolean }> {
    return this.client.delete<{ success: boolean }>(`${this.basePath}/${id}`, options);
  }
}
